import { useState } from "react";
import axios from "axios";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

const API_URL =
  "https://a9s48sduha.execute-api.ap-northeast-1.amazonaws.com/v1/daily/manage";

const PAGE_SIZE = 15;

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const btn =
    "px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 " +
    "disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-gray-100 text-sm">
      <button
        type="button"
        className={btn}
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <i className="fas fa-chevron-left" />
      </button>
      <span className="text-gray-600">
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        className={btn}
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        <i className="fas fa-chevron-right" />
      </button>
    </div>
  );
}

function DailyAttendanceReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");

  // Summary (date-wise gents/ladies count)
  const [countData, setCountData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryPage, setSummaryPage] = useState(1);

  // Details (full list for the range)
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState("");
  const [detailPage, setDetailPage] = useState(1);

  function fetch_report() {
    if (fromDate === "" || toDate === "") return;
    setLoading(true);
    setHasSearched(true);
    setActiveTab("summary");
    setSummaryPage(1);
    // Reset details so they reload for the new range when opened.
    setDetailRows([]);
    setDetailLoaded(false);
    setDetailSearch("");
    setDetailPage(1);

    axios
      .post(API_URL, { action: "COUNT", fromDate: fromDate, toDate: toDate })
      .then(function (response) {
        if (response.data.status === "SUCCESS") {
          const rows = response.data.data || [];
          setCountData(Array.isArray(rows) ? rows : []);
          setSummary(response.data.summary || null);
        } else {
          setCountData([]);
          setSummary(null);
          alert(response.data.message || "Failed to fetch report");
        }
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setCountData([]);
        setSummary(null);
        setLoading(false);
      });
  }

  function loadDetails() {
    setDetailLoading(true);
    axios
      .post(API_URL, { action: "REPORT", fromDate: fromDate, toDate: toDate })
      .then(function (response) {
        if (response.data.status === "SUCCESS") {
          const rows = response.data.data || [];
          setDetailRows(Array.isArray(rows) ? rows : []);
        } else {
          setDetailRows([]);
          alert(response.data.message || "Failed to fetch list");
        }
        setDetailLoaded(true);
        setDetailLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setDetailRows([]);
        setDetailLoaded(true);
        setDetailLoading(false);
      });
  }

  function openTab(tab) {
    setActiveTab(tab);
    if (tab === "details" && !detailLoaded && fromDate && toDate) {
      loadDetails();
    }
  }

  const detailColumns = [
    { key: "sewaDate", label: "Date" },
    { key: "sewadarBadge", label: "Badge" },
    { key: "sewadarName", label: "Sewadar Name" },
    { key: "sewaInTime", label: "In Time" },
    { key: "sewaOutTime", label: "Out Time" },
    { key: "sewaDuration", label: "Hours" },
    { key: "sewaMarkedBy", label: "Marked By" },
  ];

  const search = detailSearch.trim().toLowerCase();
  const filteredDetails = search
    ? detailRows.filter(
        (r) =>
          (r.sewadarBadge || "").toLowerCase().includes(search) ||
          (r.sewadarName || "").toLowerCase().includes(search)
      )
    : detailRows;

  const canFetch = fromDate !== "" && toDate !== "" && !loading;

  // Pagination slices (clamp page to valid range).
  const summaryTotalPages = Math.max(
    1,
    Math.ceil(countData.length / PAGE_SIZE)
  );
  const safeSummaryPage = Math.min(summaryPage, summaryTotalPages);
  const summaryStart = (safeSummaryPage - 1) * PAGE_SIZE;
  const summaryPageRows = countData.slice(summaryStart, summaryStart + PAGE_SIZE);

  const detailTotalPages = Math.max(
    1,
    Math.ceil(filteredDetails.length / PAGE_SIZE)
  );
  const safeDetailPage = Math.min(detailPage, detailTotalPages);
  const detailStart = (safeDetailPage - 1) * PAGE_SIZE;
  const detailPageRows = filteredDetails.slice(
    detailStart,
    detailStart + PAGE_SIZE
  );

  const renderCountRows = (rows, startIndex) =>
    rows.map((item, i) => (
      <tr key={item.sewaDate} className="hover:bg-gray-50">
        <td className="px-4 py-3 text-gray-400">{startIndex + i + 1}</td>
        <td className="px-4 py-3 whitespace-nowrap text-gray-800">
          {item.sewaDate}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-gray-800">
          {item.gents}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-gray-800">
          {item.ladies}
        </td>
        <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-800">
          {item.total}
        </td>
      </tr>
    ));

  const renderDetailRows = (rows, startIndex) =>
    rows.map((item, i) => (
      <tr
        key={item.sewadarBadge + item.sewaInTime + i}
        className="hover:bg-gray-50"
      >
        <td className="px-4 py-3 text-gray-400">{startIndex + i + 1}</td>
        {detailColumns.map((col) => (
          <td
            key={col.key}
            className="px-4 py-3 whitespace-nowrap text-gray-800"
          >
            {item[col.key]}
          </td>
        ))}
      </tr>
    ));

  const countHead = (
    <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
      <tr>
        <th className="px-4 py-3 whitespace-nowrap">#</th>
        <th className="px-4 py-3 whitespace-nowrap">Date</th>
        <th className="px-4 py-3 whitespace-nowrap">Gents</th>
        <th className="px-4 py-3 whitespace-nowrap">Ladies</th>
        <th className="px-4 py-3 whitespace-nowrap">Total</th>
      </tr>
    </thead>
  );

  const countFoot = summary && (
    <tfoot className="bg-gray-50 font-bold text-gray-800">
      <tr>
        <td className="px-4 py-3" colSpan={2}>
          Total
        </td>
        <td className="px-4 py-3">{summary.gents}</td>
        <td className="px-4 py-3">{summary.ladies}</td>
        <td className="px-4 py-3">{summary.total}</td>
      </tr>
    </tfoot>
  );

  const detailHead = (
    <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
      <tr>
        <th className="px-4 py-3 whitespace-nowrap">#</th>
        {detailColumns.map((col) => (
          <th key={col.key} className="px-4 py-3 whitespace-nowrap">
            {col.label}
          </th>
        ))}
      </tr>
    </thead>
  );

  const tabClass = (tab) =>
    "px-4 py-2 text-sm font-bold border-b-2 cursor-pointer transition-colors " +
    (activeTab === tab
      ? "border-blue-500 text-blue-600"
      : "border-transparent text-gray-500 hover:text-gray-700");

  return (
    <div className="grid grid-cols-12 gap-5">
      <h1 className="page-title">Daily Attendance Report</h1>

      {/* Filters */}
      <div className="col-span-12">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-12 sm:col-span-6 lg:col-span-4">
              <label className="form-label">From Date</label>
              <input
                type="date"
                className="form-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-4">
              <label className="form-label">To Date</label>
              <input
                type="date"
                className="form-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-4">
              <button
                type="button"
                disabled={!canFetch}
                onClick={() => fetch_report()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-search" />
                Fetch Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="col-span-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <img
              src="/images/loading.gif"
              alt="loading"
              className="w-12 h-12"
            />
          </div>
        ) : !hasSearched ? (
          <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <i className="fas fa-calendar-day text-4xl mb-3 block" />
            Choose a date range and fetch the report.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-2">
              <button
                type="button"
                className={tabClass("summary")}
                onClick={() => openTab("summary")}
              >
                <i className="fas fa-chart-column mr-2" />
                Summary
              </button>
              <button
                type="button"
                className={tabClass("details")}
                onClick={() => openTab("details")}
              >
                <i className="fas fa-list mr-2" />
                Details
              </button>
            </div>

            {/* Summary tab */}
            {activeTab === "summary" &&
              (countData.length > 0 ? (
                <>
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                    <span className="text-sm font-semibold text-gray-600">
                      {countData.length} day
                      {countData.length === 1 ? "" : "s"} · Gents{" "}
                      {summary ? summary.gents : 0} · Ladies{" "}
                      {summary ? summary.ladies : 0} · Total{" "}
                      {summary ? summary.total : 0}
                    </span>
                    <ReactHTMLTableToExcel
                      id="daily-count-export-btn"
                      table="daily-count-table-export"
                      filename={`daily-attendance-count_${fromDate}_to_${toDate}`}
                      sheet="Daily Count"
                      buttonText="Export to Excel"
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2 px-4 rounded shadow cursor-pointer"
                    />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      {countHead}
                      <tbody className="divide-y divide-gray-100">
                        {renderCountRows(summaryPageRows, summaryStart)}
                      </tbody>
                      {countFoot}
                    </table>
                  </div>
                  <Pagination
                    page={safeSummaryPage}
                    totalPages={summaryTotalPages}
                    onChange={setSummaryPage}
                  />
                </>
              ) : (
                <div className="text-center text-gray-400 py-12">
                  No attendance records found for the selected range.
                </div>
              ))}

            {/* Details tab */}
            {activeTab === "details" &&
              (detailLoading ? (
                <div className="flex justify-center py-12">
                  <img
                    src="/images/loading.gif"
                    alt="loading"
                    className="w-12 h-12"
                  />
                </div>
              ) : detailRows.length > 0 ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                    <input
                      type="text"
                      className="form-input sm:max-w-xs"
                      placeholder="Search badge or name..."
                      value={detailSearch}
                      onChange={(e) => {
                        setDetailSearch(e.target.value);
                        setDetailPage(1);
                      }}
                    />
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        {filteredDetails.length} entr
                        {filteredDetails.length === 1 ? "y" : "ies"}
                      </span>
                      <ReactHTMLTableToExcel
                        id="daily-detail-export-btn"
                        table="daily-detail-table-export"
                        filename={`daily-attendance-list_${fromDate}_to_${toDate}`}
                        sheet="Daily List"
                        buttonText="Export to Excel"
                        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2 px-4 rounded shadow cursor-pointer"
                      />
                    </div>
                  </div>
                  {filteredDetails.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                          {detailHead}
                          <tbody className="divide-y divide-gray-100">
                            {renderDetailRows(detailPageRows, detailStart)}
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        page={safeDetailPage}
                        totalPages={detailTotalPages}
                        onChange={setDetailPage}
                      />
                    </>
                  ) : (
                    <div className="text-center text-gray-400 py-12">
                      No entries match "{detailSearch}".
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-gray-400 py-12">
                  No attendance records found for the selected range.
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Hidden full tables — used only for complete Excel exports */}
      <table id="daily-count-table-export" className="hidden">
        {countHead}
        <tbody>{renderCountRows(countData, 0)}</tbody>
        {countFoot}
      </table>
      <table id="daily-detail-table-export" className="hidden">
        {detailHead}
        <tbody>{renderDetailRows(filteredDetails, 0)}</tbody>
      </table>
    </div>
  );
}

export default DailyAttendanceReport;
